import { lineIntersect, lineString } from '@turf/turf';
import { Feature, LineString, MultiPolygon, Point, Polygon } from 'geojson';
import { getSortedDistancePairsFromPoint } from './lines';

interface GetGuidelinesProps {
  rowPoints: Feature<Point>[];
  startPoint: Feature<Point>;
  endPointExcl: Feature<Point>;
  polygon: Feature<Polygon | MultiPolygon>;
}

/* Splits the line between a start point (inside the polygon) and an endpoint
 * (outside the polygon) into several guidelines, based on the intersection
 * point(s) with the polygon. Each guideline extends from a row point inside
 * the polygon to an intersection point with the polygon. This function returns
 * the guidelines, plus the given rowPoints, which are partitioned into groups
 * that fall along each guideline.
 */
export const getGuidelines = ({
  rowPoints, // All rowPoints are assumed to be inside polygon
  startPoint, // Start point of the guidelines, assumed to be inside polygon
  endPointExcl, // End point of the guidelines, assumed to be outside polygon
  polygon,
}: GetGuidelinesProps) => {
  const intersectionLine = lineString([
    startPoint.geometry.coordinates, // First grid point inside the polygon
    endPointExcl.geometry.coordinates, // First grid point outside the polygon
  ]);
  // Find the intersections with the polygon
  const intersections = lineIntersect(intersectionLine, polygon);
  const intersectionDistancePairs = getSortedDistancePairsFromPoint(
    intersections.features,
    startPoint,
  );
  const rowPointDistancePairs = getSortedDistancePairsFromPoint(
    rowPoints,
    startPoint,
  );
  let rowPointIndex = 0;
  const guidelines = <Feature<LineString>[]>[];
  const partitionedRowPoints = <Feature<Point>[][]>[];
  intersectionDistancePairs.forEach(([intersectionDist, intersection], i) => {
    // This intersection is an exit point from polygon
    if (i % 2 === 0) {
      const [rowPointDist, rowPoint] =
        rowPointDistancePairs[rowPointIndex] || [];
      // Do not add guideline if row point is further than intersection point
      if (rowPoint == null || intersectionDist < rowPointDist) {
        return;
      }
      // Add guideline from row point to intersection point
      guidelines.push(
        lineString([
          rowPoint.geometry.coordinates,
          intersection.geometry.coordinates,
        ]),
      );
      // Add all row points along this guideline to the next partition
      const partition = <Feature<Point>[]>[];
      for (let j = rowPointIndex; j < rowPoints.length; j += 1) {
        const [dist, point] = rowPointDistancePairs[j];
        // First row point beyond intersection
        if (dist > intersectionDist) {
          rowPointIndex = j; // Start of next guideline
          break;
        } else {
          partition.push(point);
        }
      }
      partitionedRowPoints.push(partition);
    }
  });
  return { guidelines, partitionedRowPoints };
};
